import React from 'react';
import { Box, Typography, Table, TableBody, TableCell, TableHead, TableRow, Divider } from '@mui/material';
import { QRCodeSVG as QRCode } from 'qrcode.react';

const InvoicePrint = ({ invoice, client, lineItems, company }) => {
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const balanceDue = (invoice.total_amount || 0) - (invoice.amount_paid || 0);

  return (
    <Box className="invoice-print">
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 4, pb: 2, borderBottom: '3px solid #1976d2' }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4" sx={{ color: '#1976d2', fontWeight: 'bold', mb: 1 }}>
            {company?.company_name || 'PaceEstate'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {company?.company_address || '123 Business Ave'}<br />
            {company?.company_city || 'Tampa, FL 33601'}<br />
            Phone: {company?.company_phone || '(813) 555-1234'}<br />
            Email: {company?.company_email || 'info@paceestate.com'}<br />
            {company?.company_website || 'www.paceestate.com'}
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 1 }}>
            INVOICE
          </Typography>
          <Typography variant="body2">
            <strong>Invoice #:</strong> {invoice.invoice_number}<br />
            <strong>Date:</strong> {formatDate(invoice.created_at)}<br />
            <strong>Due Date:</strong> {invoice.due_date ? formatDate(invoice.due_date) : 'Upon Receipt'}<br />
            {invoice.status && (
              <>
                <strong>Status:</strong> <span style={{ textTransform: 'uppercase' }}>{invoice.status}</span>
              </>
            )}
          </Typography>
        </Box>
      </Box>

      {/* Bill To Section */}
      <Box sx={{ bgcolor: '#f5f5f5', p: 2, mb: 3, borderLeft: '4px solid #1976d2' }}>
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          BILL TO:
        </Typography>
        <Typography variant="body2">
          <strong>{client?.name}</strong><br />
          {client?.address && <>{client.address}<br /></>}
          {client?.city && <>{client.city}, {client.state} {client.zip_code}<br /></>}
          {client?.email && <>Email: {client.email}<br /></>}
          {client?.phone && <>Phone: {client.phone}</>}
        </Typography>
      </Box>

      {/* Line Items */}
      <Table sx={{ mb: 3 }}>
        <TableHead>
          <TableRow sx={{ bgcolor: '#1976d2' }}>
            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Description</TableCell>
            <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold', width: 100 }}>Quantity</TableCell>
            <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold', width: 120 }}>Unit Price</TableCell>
            <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold', width: 120 }}>Amount</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {lineItems?.map((item, index) => (
            <TableRow key={index}>
              <TableCell>
                {item.description}
                {item.taxable && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    (Taxable)
                  </Typography>
                )}
              </TableCell>
              <TableCell align="center">{item.quantity}</TableCell>
              <TableCell align="right">{formatCurrency(item.unit_price)}</TableCell>
              <TableCell align="right">{formatCurrency(item.total)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Totals */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 4 }}>
        <Box sx={{ width: 300 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography fontWeight="bold">Subtotal:</Typography>
            <Typography>{formatCurrency(invoice.subtotal)}</Typography>
          </Box>

          {invoice.tax_amount > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography fontWeight="bold">
                Tax:
                {invoice.tax_county && (
                  <Typography component="span" variant="caption" color="text.secondary">
                    {' '}({invoice.tax_county} County)
                  </Typography>
                )}
              </Typography>
              <Typography>{formatCurrency(invoice.tax_amount)}</Typography>
            </Box>
          )}

          {invoice.commission_amount > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography fontWeight="bold">Commission:</Typography>
              <Typography>{formatCurrency(invoice.commission_amount)}</Typography>
            </Box>
          )}

          <Divider sx={{ my: 1, borderWidth: 2, borderColor: '#333' }} />

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="h6" fontWeight="bold">TOTAL:</Typography>
            <Typography variant="h6" fontWeight="bold">
              {formatCurrency(invoice.total_amount)}
            </Typography>
          </Box>

          {invoice.amount_paid > 0 && (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography fontWeight="bold">Amount Paid:</Typography>
                <Typography>{formatCurrency(invoice.amount_paid)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="h6" fontWeight="bold" color="error">
                  BALANCE DUE:
                </Typography>
                <Typography variant="h6" fontWeight="bold" color="error">
                  {formatCurrency(balanceDue)}
                </Typography>
              </Box>
            </>
          )}
        </Box>
      </Box>

      {/* Notes */}
      {invoice.notes && (
        <Box sx={{ bgcolor: '#f5f5f5', p: 2, mb: 3 }}>
          <Typography fontWeight="bold" gutterBottom>Notes:</Typography>
          <Typography variant="body2">{invoice.notes}</Typography>
        </Box>
      )}

      {/* Payment Terms */}
      <Box sx={{ bgcolor: '#fff3cd', p: 2, mb: 3, borderLeft: '4px solid #ffc107' }}>
        <Typography fontWeight="bold" gutterBottom>Payment Terms:</Typography>
        <Typography variant="body2">
          {invoice.terms || 'Payment due upon receipt. Thank you for your business!'}
        </Typography>
      </Box>

      {/* QR Code */}
      <Box sx={{ textAlign: 'center', mb: 3 }}>
        <QRCode value={`INV-${invoice.invoice_number}`} size={100} />
        <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
          Scan to view invoice details
        </Typography>
      </Box>

      {/* Footer */}
      <Box sx={{ textAlign: 'center', pt: 2, borderTop: '1px solid #ccc', className: 'print-footer' }}>
        <Typography variant="caption" color="text.secondary">
          Thank you for your business!<br />
          Questions? Contact us at {company?.company_email || 'info@paceestate.com'} or {company?.company_phone || '(813) 555-1234'}
        </Typography>
      </Box>
    </Box>
  );
};

export default InvoicePrint;
